import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

import Topbar from '../main/Topbar';
import ProjectSidebar from '../../components/ProjectSidebar';
import { ProjectDetail } from '../../utils/commonUtils';
import { ShopFormUtils } from '../../utils/shopCommon';
import {
  createNotificationChannelSetting,
  NOTIFICATION_CHANNEL_OPTIONS,
  type NotificationChannelSettingCreateRequest,
} from '../../utils/notificationChannelSetting';
import { ArrowLeft, Bell, RefreshCw } from 'lucide-react';

interface FormErrors {
  channel?: string;
  senderKey?: string;
}

const FormRow: React.FC<{
  label: string;
  required?: boolean;
  hint?: string;
  children: React.ReactNode;
  error?: string;
}> = ({ label, required, hint, children, error }) => (
  <div className="flex flex-col sm:flex-row sm:items-center gap-3 py-3 border-b border-gray-100 last:border-0">
    <div className="w-52 sm:w-56 flex-shrink-0">
      <p className="text-sm font-medium text-gray-700">
        {label}
        {required && <span className="text-red-500 ml-0.5">*</span>}
      </p>
      {hint && <p className="mt-0.5 text-xs text-gray-500">{hint}</p>}
    </div>
    <div className="flex-1 min-w-0">
      {children}
      {error && <p className="mt-1 text-sm text-red-600">{error}</p>}
    </div>
  </div>
);

const OptionButtons: React.FC<{
  options: { value: string; label: string }[];
  value: string;
  onChange: (value: string) => void;
}> = ({ options, value, onChange }) => (
  <div className="flex flex-wrap gap-2">
    {options.map((opt) => {
      const selected = value === opt.value;
      return (
        <button
          key={opt.value}
          type="button"
          onClick={() => onChange(opt.value)}
          className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${
            selected ? 'bg-blue-600 text-white hover:bg-blue-700' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
          }`}
        >
          <span className={`w-2 h-2 rounded-full ${selected ? 'bg-white' : 'bg-gray-400'}`} />
          {opt.label}
        </button>
      );
    })}
  </div>
);

const NotificationChannelCreate: React.FC = () => {
  const navigate = useNavigate();
  const { projectCode: projectCodeFromUrl } = useParams<{ projectCode: string }>();

  const [isDesktopSidebarOpen, setIsDesktopSidebarOpen] = useState(true);
  const [isMobileSidebarOpen, setIsMobileSidebarOpen] = useState(false);
  const [isProfileDropdownOpen, setIsProfileDropdownOpen] = useState(false);
  const [activeMenu, setActiveMenu] = useState('notification-channel-list');

  const [formData, setFormData] = useState({
    channel: 'ALIMTALK',
    senderKey: '',
  });
  const [errors, setErrors] = useState<FormErrors>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [projectDetail, setProjectDetail] = useState<ProjectDetail | null>(null);

  const listPath = `/projects/${projectCodeFromUrl}/notification-channel-settings`;

  ShopFormUtils.useMobileSidebarResize(setIsMobileSidebarOpen);

  useEffect(() => {
    if (!projectCodeFromUrl) {
      navigate('/projects', { replace: true });
      return;
    }
    const projectInfo = ShopFormUtils.loadProjectDetail();
    if (!projectInfo) {
      navigate('/projects', { replace: true });
      return;
    }
    const detail: ProjectDetail = {
      id: (projectInfo as { id?: string }).id ?? projectInfo.code ?? '',
      ...projectInfo,
    };
    if (detail.code !== projectCodeFromUrl) {
      navigate('/projects', { replace: true });
      return;
    }
    setProjectDetail(detail);
  }, [projectCodeFromUrl, navigate]);

  const validate = (): boolean => {
    const next: FormErrors = {};
    if (!formData.channel) next.channel = '채널을 선택하세요.';
    if (!formData.senderKey.trim()) next.senderKey = '발송키는 필수입니다.';
    setErrors(next);
    return Object.keys(next).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitError(null);
    if (!validate() || !projectCodeFromUrl) return;
    try {
      setIsSubmitting(true);
      const payload: NotificationChannelSettingCreateRequest = {
        channel: formData.channel,
        senderKey: formData.senderKey.trim(),
      };
      await createNotificationChannelSetting(projectCodeFromUrl, payload);
      navigate(listPath, { replace: true });
    } catch (err) {
      setSubmitError(err instanceof Error ? err.message : '알림 채널 생성에 실패했습니다.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleReset = () => {
    setFormData({
      channel: 'ALIMTALK',
      senderKey: '',
    });
    setErrors({});
    setSubmitError(null);
  };

  if (!projectDetail) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="text-center">
          <p className="text-gray-500">프로젝트 정보를 불러올 수 없습니다.</p>
          <button
            type="button"
            onClick={() => navigate('/projects')}
            className="mt-4 text-blue-600 hover:text-blue-700"
          >
            프로젝트 목록으로 돌아가기
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="h-screen bg-gray-50 flex overflow-hidden">
      <ProjectSidebar
        isDesktopSidebarOpen={isDesktopSidebarOpen}
        setIsDesktopSidebarOpen={setIsDesktopSidebarOpen}
        isMobileSidebarOpen={isMobileSidebarOpen}
        setIsMobileSidebarOpen={setIsMobileSidebarOpen}
        activeMenu={activeMenu}
        setActiveMenu={setActiveMenu}
        projectDetail={projectDetail}
      />

      <div className="flex-1 flex flex-col min-w-0 overflow-hidden">
        <Topbar
          title="알림 채널 생성"
          subtitle={
            projectCodeFromUrl
              ? `${projectDetail.name} (${projectCodeFromUrl})`
              : '새 알림 채널을 등록합니다'
          }
          isMobileSidebarOpen={isMobileSidebarOpen}
          setIsMobileSidebarOpen={setIsMobileSidebarOpen}
          isProfileDropdownOpen={isProfileDropdownOpen}
          setIsProfileDropdownOpen={setIsProfileDropdownOpen}
          showMobileLogo={true}
          projectDetail={projectDetail}
        />

        <main className="flex-1 p-4 sm:p-6 overflow-y-auto">
          <div className="max-w-4xl mx-auto">
            <div className="mb-6">
              <button
                type="button"
                onClick={() => navigate(listPath)}
                className="inline-flex items-center gap-2 text-sm text-gray-600 hover:text-gray-900"
              >
                <ArrowLeft className="w-4 h-4" />
                알림 채널 목록으로
              </button>
            </div>

            <form onSubmit={handleSubmit}>
              {submitError && (
                <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">
                  {submitError}
                </div>
              )}

              <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
                <div className="px-4 lg:px-6 py-4 border-b border-gray-200">
                  <h2 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
                    <Bell className="w-5 h-5" />
                    기본 정보
                  </h2>
                </div>
                <div className="px-4 lg:px-6 py-2 space-y-0">
                  <FormRow label="채널" required error={errors.channel}>
                    <OptionButtons
                      options={[...NOTIFICATION_CHANNEL_OPTIONS]}
                      value={formData.channel}
                      onChange={(v) => {
                        setFormData((p) => ({ ...p, channel: v }));
                        setErrors((e2) => ({ ...e2, channel: undefined }));
                      }}
                    />
                  </FormRow>
                  <FormRow
                    label="발송키"
                    required
                    hint="알림톡은 BizGo senderKey를 입력하세요."
                    error={errors.senderKey}
                  >
                    <input
                      type="text"
                      value={formData.senderKey}
                      onChange={(e) => {
                        setFormData((p) => ({ ...p, senderKey: e.target.value }));
                        setErrors((e2) => ({ ...e2, senderKey: undefined }));
                      }}
                      placeholder="발송키를 입력하세요"
                      className={`block w-full min-w-[200px] px-3 py-2 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                        errors.senderKey ? 'border-red-500' : 'border-gray-300'
                      }`}
                    />
                  </FormRow>
                </div>
              </div>

              <div className="flex justify-between items-center mt-6">
                <button
                  type="button"
                  onClick={handleReset}
                  className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 flex items-center text-sm font-medium text-gray-700"
                >
                  <RefreshCw className="w-4 h-4 mr-2" />
                  초기화
                </button>
                <div className="flex gap-3">
                  <button
                    type="button"
                    onClick={() => navigate(listPath)}
                    className="px-6 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 text-sm font-medium text-gray-700"
                  >
                    취소
                  </button>
                  <button
                    type="submit"
                    disabled={isSubmitting}
                    className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center text-sm font-medium"
                  >
                    {isSubmitting ? '생성 중...' : '알림 채널 생성'}
                  </button>
                </div>
              </div>
            </form>
          </div>
        </main>
      </div>
    </div>
  );
};

export default NotificationChannelCreate;
